import { PDFText } from './pdfModal';

export interface SignificantLevel {
  color: 'green' | 'yellow' | 'orange' | 'red';
  isSignificant: boolean;
}

export type CalculationType =
  | 'addition'
  | 'subtraction'
  | 'multiplication'
  | 'division';

export interface Question {
  question: string;
  questionNotes: string[];
  answer: string;
  answerNotes: string[];
}

export interface QuestionWithMarks extends Question {
  marks: number;
}

export interface FormModel {
  title: string;
  description: string;
  riskQuestions: Question[];
  compulsoryQuestions: Question[];
  impactQuestions: QuestionWithMarks[];
  probabilityQuestions: QuestionWithMarks[];
  marksNotes: string[];
  isSignificant: SignificantLevel | null;

  getImpactMarks: () => number;
  getProbabilityMarks: () => number;
  getTotalMarks: (_type?: CalculationType, _isReverse?: boolean) => number;
}

export interface QuestionPDF {
  questionNumber: number;
  yPos: number;
  position: QuestionPosition;
  texts: PDFText[];
}

export interface QuestionPosition {
  top: number;
  bottom: number;
}

export class QuestionForm implements FormModel {
  title: string;
  description: string;
  riskQuestions: Question[] = [];
  compulsoryQuestions: Question[] = [];
  impactQuestions: QuestionWithMarks[] = [];
  probabilityQuestions: QuestionWithMarks[] = [];
  marksNotes: string[] = [];
  isSignificant: SignificantLevel | null = null;

  constructor(
    title?: string,
    description?: string,
    compulsoryQuestions?: Question[],
    impactQuestions?: QuestionWithMarks[],
    probabilityQuestions?: QuestionWithMarks[]
  ) {
    this.title = title || '';
    this.description = description || '';
    this.compulsoryQuestions = compulsoryQuestions || [];
    this.impactQuestions = impactQuestions || [];
    this.probabilityQuestions = probabilityQuestions || [];
  }

  getImpactMarks = () => {
    const allImpactMarks = this.impactQuestions.map((q) => q.marks);
    return (
      allImpactMarks.reduce((a, b) => a + b, 0) / this.impactQuestions.length
    );
  };
  getProbabilityMarks = () => {
    const allProbabilityMarks = this.probabilityQuestions.map((q) => q.marks);
    return allProbabilityMarks.reduce((a, b) => a + b, 0);
  };
  /**
   *
   * @param type Default "multiplication"
   *
   * Can be "addition", "subtraction", "multiplication" or "division". Default "multiplication"
   * @param isReverse Default false
   *
   * If true item1 will be the impactMarks, item2 will be the probabilityMarks
   *
   * If false item1 will be the probabilityMarks, item2 will be the impactMarks
   * @returns Default 0
   *
   * Returns item1 <type> item2
   */
  getTotalMarks = (
    type: CalculationType = 'multiplication',
    isReverse: boolean = false
  ) => {
    const item1 = isReverse
      ? this.getProbabilityMarks()
      : this.getImpactMarks();
    const item2 = isReverse
      ? this.getImpactMarks()
      : this.getProbabilityMarks();

    let result = 0;

    switch (type) {
      case 'addition':
        result = isNaN(item1 + item2) ? 0 : item1 + item2;
        break;
      case 'subtraction':
        result = isNaN(item1 - item2) ? 0 : item1 - item2;
        break;
      case 'multiplication':
        result = isNaN(item1 * item2) ? 0 : item1 * item2;
        break;
      case 'division':
        result = isNaN(item1 / item2) ? 0 : item1 / item2;
        break;
    }

    if (result >= 1 && result <= 5) {
      this.isSignificant = { color: 'green', isSignificant: false };
    } else if (result >= 6 && result <= 12) {
      this.isSignificant = { color: 'yellow', isSignificant: false };
    } else if (result >= 13 && result <= 20) {
      this.isSignificant = { color: 'orange', isSignificant: true };
    } else if (result >= 21 && result <= 25) {
      this.isSignificant = { color: 'red', isSignificant: true };
    }

    return result;
  };
}
